import type { InstrumentCoverage } from './types'

export const UNBOUNDED_NOTE = 'No stored-data span for this source; the run fails if the range is not covered.'

export interface DateBounds {
  /** Inclusive ISO bounds; `null` is open on that side. */
  min: string | null
  max: string | null
  /** Set when the instrument has no coverage at all (MT5, TradingView): both sides open. */
  note: string | null
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
const day = (value: string | null | undefined): string | null =>
  typeof value === 'string' && ISO_DAY.test(value) ? value : null

/**
 * Spec 073 A1/B2: the span a backtest may read. `backtest_*` first, falling back
 * to the 1-minute `first_day`/`last_complete_day`; a null side is open on that
 * side, never broken. No coverage row means unbounded with a note.
 */
export function dateBounds(row: InstrumentCoverage | null): DateBounds {
  if (row === null) return { min: null, max: null, note: UNBOUNDED_NOTE }
  return {
    min: day(row.backtest_first_day) ?? day(row.first_day),
    max: day(row.backtest_last_day) ?? day(row.last_complete_day),
    note: null,
  }
}

/** The inline message for a held date that is not a real ISO day, or `undefined`. An empty value is not flagged. */
export function dateTextError(value: unknown): string | undefined {
  if (typeof value !== 'string' || value === '') return undefined
  if (!ISO_DAY.test(value)) return 'Enter a date as DD.MM.YYYY'
  const [y, m, d] = value.split('-').map(Number)
  const real = new Date(Date.UTC(y, m - 1, d))
  return real.getUTCFullYear() === y && real.getUTCMonth() === m - 1 && real.getUTCDate() === d
    ? undefined
    : 'Enter a date as DD.MM.YYYY'
}

const display = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`

/**
 * A blocking row error for a real ISO day outside the stored span (the calendar disables the same days);
 * `undefined` when in range, malformed (`dateTextError` owns that) or when the instrument is unbounded.
 */
export function dateRangeError(value: unknown, bounds: DateBounds): string | undefined {
  if (typeof value !== 'string' || !ISO_DAY.test(value)) return undefined
  const below = bounds.min !== null && value < bounds.min
  const above = bounds.max !== null && value > bounds.max
  if (!below && !above) return undefined
  if (bounds.min !== null && bounds.max !== null) return `Stored data covers ${display(bounds.min)} to ${display(bounds.max)}`
  return bounds.min !== null ? `Stored data starts ${display(bounds.min)}` : `Stored data ends ${display(bounds.max as string)}`
}
